import { Key } from "react";

/** Mirrors Vero1Checklist.Status on the backend. */
export enum Vero1ChecklistStatus {
  Draft = "DRAFT",
  Submitted = "SUBMITTED",
  NeedsReReview = "NEEDS_REREVIEW",
  Superseded = "SUPERSEDED",
  Discarded = "DISCARDED",
  Invalidated = "INVALIDATED",
}

/** Mirrors Vero1Checklist.Recommendation. AGREE is not an override. */
export enum Vero1Recommendation {
  Agree = "AGREE",
  Approve = "APPROVE",
  Conditional = "CONDITIONAL",
  Decline = "DECLINE",
  Cancel = "CANCEL",
}

/** Mirrors Vero1Checklist.CancelReason — why the process ended (spec 6). */
export enum Vero1CancelReason {
  Unresponsive = "UNRESPONSIVE",
  Withdrew = "WITHDREW",
  UnitUnavailable = "UNIT_UNAVAILABLE",
  Duplicate = "DUPLICATE",
  Other = "OTHER",
}

export const RECOMMENDATION_LABELS: Record<string, string> = {
  [Vero1Recommendation.Agree]: "Agree with Lumen",
  [Vero1Recommendation.Approve]: "Approve",
  [Vero1Recommendation.Conditional]: "Conditional",
  [Vero1Recommendation.Decline]: "Decline",
  [Vero1Recommendation.Cancel]: "Cancel",
};

/** The client's authored word for a submitted recommendation, or null when
 * they configured none and the caller should print Vero's enum label.
 *
 * The server resolves the AGREE indirection (`effective_recommendation_label`)
 * so no surface re-implements it; this only normalises blank to null. Shared
 * by every surface so one payload cannot render two different words — the
 * agreement `collapse_vero1_banner_state` promises.
 */
export const resolveVero1RecommendationName = (source: {
  /** Banner and deals-list payloads name it this way... */
  recommendationLabel?: string;
  /** ...checklist payloads this way. Both are already resolved server-side. */
  effectiveRecommendationLabel?: string;
}): string | null =>
  source.recommendationLabel?.trim() ||
  source.effectiveRecommendationLabel?.trim() ||
  null;

export const CANCEL_REASON_LABELS: Record<string, string> = {
  [Vero1CancelReason.Unresponsive]: "Applicant unresponsive",
  [Vero1CancelReason.Withdrew]: "Applicant withdrew",
  [Vero1CancelReason.UnitUnavailable]: "Unit no longer available",
  [Vero1CancelReason.Duplicate]: "Duplicate application",
  [Vero1CancelReason.Other]: "Other",
};

/** Canonical outcomes original_outcome may hold — mapped from the run's
 *  scoreLabelColor, never parsed from the client-configurable label text. */
export type Vero1CanonicalOutcome =
  | Vero1Recommendation.Approve
  | Vero1Recommendation.Conditional
  | Vero1Recommendation.Decline;

/** Deal.vero1_banner_state — null on a non-VERO1 deal. */
export type Vero1BannerState =
  | { state: "unable_to_verify_identity" }
  | { state: "pending_review"; runAt?: string | null }
  | { state: "under_review"; startedAt: string | null; runAt?: string | null }
  | {
      state: "recommendation";
      recommendation: Vero1Recommendation | null;
      /** Server-resolved display word: the agent's chosen name, or the run's
       * own label when they agreed. "" when the client configured none, where
       * the enum label carries it. */
      recommendationLabel?: string;
      submittedAt: string | null;
    }
  | {
      state: "override_not_applied";
      recommendation: null;
      submittedAt: string | null;
    }
  /** A submitted review with an applicant outstanding: not governing until the
   * whole household has submitted and screening has read it. */
  | { state: "awaiting_applicants"; submittedAt: string | null };

export enum Vero1ItemType {
  Criterion = "LUMEN_CRITERION",
  Custom = "CUSTOM",
}

/** Mirrors Vero1ChecklistItem.RowKind. */
export enum Vero1RowKind {
  Outcome = "OUTCOME",
  Fact = "FACT",
  Custom = "CUSTOM",
  Sentilink = "SENTILINK",
}

/** Mirrors Vero1ChecklistItem.Severity. */
export type Vero1Severity = "pass" | "warn" | "fail";

export const SEVERITY_ORDER: readonly Vero1Severity[] = [
  "pass",
  "warn",
  "fail",
];

export interface Vero1OutcomeOption {
  name: string;
  severity: Vero1Severity;
  label: string;
  recommendation?: string | null;
}

export interface Vero1OutcomeOptions {
  outcomes?: Vero1OutcomeOption[];
  fired?: string[];
  scoreLabels?: Partial<Record<Vero1Severity, string | null>>;
}

/** Lumen's severity lives in originalSeverity, the agent's pick in overrideSeverity. */
export interface Vero1ChecklistItem {
  id: Key;
  itemType: Vero1ItemType;
  rowKind: Vero1RowKind;
  criterionKey: string;
  isAnnotationOnly?: boolean;
  category: string;
  label: string;
  application: Key | null;
  /** Whose row this is — null on deal-level rows. Multi-applicant deals
   *  repeat every per-applicant label, so rows must say whose they are. */
  applicantName?: string | null;
  /** Null when the fact is missing or the check was not evaluated. */
  originalSeverity: Vero1Severity | null;
  originalValue: unknown;
  originalExplanation: string;
  outcomeOptions: Vero1OutcomeOptions;
  availableSeverities?: Vero1Severity[];
  /** Configured names per severity. More than one is a pick the agent owes. */
  nameCandidates?: Partial<Record<Vero1Severity, string[]>>;
  overrideSeverity: Vero1Severity | null;
  overrideOutcomeName?: string;
  reasonCode: string;
  /** Short custom label carried by the OTHER reason code. Never letter-facing. */
  otherReasonLabel: string;
  /** Seeded from a checklist template — optional at submit, unlike rows the
   *  agent added deliberately. */
  fromTemplate?: boolean;
  overrideExplanation: string;
  /** What leasing must do about the finding. Client-visible, unlike the note. */
  actionForLeasing: string;
  /** Vero-internal; absent on the client-staff serializer. */
  internalNote?: string;
  persistForProperty: boolean;
  /** Server-computed completeness verdicts (agent payloads only — stripped
   *  for client staff). The panel reads these instead of recomputing the
   *  model's predicates, so the UI and the submit gate cannot disagree. */
  isEdited?: boolean;
  resultMoved?: boolean;
  needsReason?: boolean;
  needsFindings?: boolean;
  needsAssessment?: boolean;
  needsName?: boolean;
  changedVsPriorRun: boolean | null;
  adoptedFromVersion: number | null;
  lastEditedBy: Key | null;
  lastEditedByName: string | null;
  lastEditedAt: string | null;
}

export interface Vero1Checklist {
  id: Key;
  deal: Key;
  version: number;
  predecessor: Key | null;
  status: Vero1ChecklistStatus;
  baseRunId: string | null;
  baseRunTimestamp: string | number | null;
  /** Agent payloads only — the client serializer omits the delta fields. */
  originalRecommendation?: string | null;
  /** Canonical severity of Lumen's result (from scoreLabelColor); null when
   *  the run carried no recognizable color. */
  originalOutcome?: Vero1CanonicalOutcome | null;
  finalRecommendation: Vero1Recommendation | null;
  /** The client's word for the pick; blank until chosen. */
  finalRecommendationLabel?: string;
  /** Server-resolved display word: the pick's name, or the run's own label
   *  when the agent agreed. Blank when the client configured none. */
  effectiveRecommendationLabel?: string;
  /** Server-resolved severity — AGREE takes Lumen's own outcome colour. */
  effectiveRecommendationColour?: Vero1Severity | null;
  /** Configured labels keyed by severity — APPROVE→pass, CONDITIONAL→warn,
   *  DECLINE→fail (the server's _RECOMMENDATION_COLOUR). */
  recommendationLabelCandidates?: Partial<Record<Vero1Severity, string[]>>;
  recommendationNeedsLabel?: boolean;
  cancelReason: Vero1CancelReason | "";
  isOverride?: boolean;
  instructionLine: string;
  decisionRationale: string;
  clientFindings: string;
  /** The derived run never landed — the deal still shows the automated score. */
  deriveFailed: boolean;
  startedBy: Key | null;
  startedByName: string | null;
  lastEditedBy: Key | null;
  lastEditedByName: string | null;
  submittedBy: Key | null;
  submittedByName: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  items: Vero1ChecklistItem[];
}

/** Mirrors the STATE_* constants in vero1_checklist_generation. */
export enum Vero1GenerationStatus {
  NoRun = "no-run",
  RunInProgress = "run-in-progress",
  Ready = "ready",
  LumenUnavailable = "lumen-unavailable",
  DealDecisioned = "deal-decisioned",
  NoOutcomes = "no-outcomes",
  ApplicantsPending = "applicants-pending",
  RescreeningForReview = "rescreening-for-review",
  NoLumenConfig = "no-lumen-config",
}

export interface Vero1GenerationState {
  state: Vero1GenerationStatus;
  /** Null when the run carries no timestamp to compare against. */
  formulaChangedSinceRun: boolean | null;
  /** Identity of the newest completed run — a change proves the read moved
   *  past whatever snapshot a start conflicted on. */
  latestRunId?: string | null;
}

export interface Vero1ReasonCatalog {
  /** Category → offerable codes. Safe under the camelize transform (codes are
   *  array VALUES). Never consume `directions` — its keys ARE the codes and
   *  the axios layer camelCases response keys. */
  byCategory: Record<string, string[]>;
  uncategorized: string[];
  /** Leasing-facing wording per code. Pairs, not a map: the axios layer
   *  camelCases response keys, which would mangle the codes. */
  labels?: { code: string; label: string }[];
}

/** One row the newer run moved — feeds the deal review tab's per-item
 *  "New Lumen result" badges (PRODUCT-2060). */
export interface Vero1RereviewChangedRow {
  criterionKey: string;
  applicationId: Key | null;
  /** Null when the row disappeared in the new run. */
  label: string | null;
  freshSeverity: Vero1Severity | null;
  freshValue: unknown;
}

/** Agent-only: the run that landed after a submitted review (spec 4.4 band).
 *  Dismissal is per run — a newer run has a new id, so the band returns. */
export interface Vero1RereviewNotice {
  runId: string;
  /** Run timestamp in ms epoch; null when the run carried none. */
  runAt: number | null;
  /** Assessed rows the newer run moved; null when indeterminate. */
  changedCount: number | null;
  changedRows?: Vero1RereviewChangedRow[] | null;
}

/** Version-picker row for the deal review tab — deliberately item-free. */
export interface Vero1ChecklistVersionSummary {
  id: Key;
  version: number;
  status: Vero1ChecklistStatus;
  finalRecommendation: Vero1Recommendation | null;
  /** Server-resolved display word: the pick's name, or the run's own label
   *  when the agent agreed. Blank when the client configured none. */
  effectiveRecommendationLabel?: string;
  /** Server-resolved severity — AGREE takes Lumen's own outcome colour. */
  effectiveRecommendationColour?: Vero1Severity | null;
  cancelReason: Vero1CancelReason | "";
  isOverride: boolean;
  submittedByName: string | null;
  submittedAt: string | null;
  baseRunId: string | null;
  baseRunTimestamp: string | number | null;
}

/** Run-history row for the deal review tab's Lumen section — the lumen
 *  service's summary joined with Django provenance where a mirror exists. */
export interface DealLumenRunSummary {
  id: string;
  timestamp: number;
  scoreLabel: string | null;
  hasError: boolean;
  vero1Derived: boolean;
  checklistVersion: number | null;
  executedByName: string | null;
  triggerEvent: number | null;
}

export interface Vero1ChecklistPayload {
  checklist: Vero1Checklist | null;
  reviewInProgress: boolean;
  /** Agent-only. */
  generation?: Vero1GenerationState;
  /** Agent-only: present while the current review is NEEDS_REREVIEW. */
  rereview?: Vero1RereviewNotice | null;
  /** Agent-only: server-enforced reason vocabulary. */
  reasonCodes?: Vero1ReasonCatalog;
  /** Agent-only: which persist scopes the add-field picker offers. */
  persistScopeOptions?: Vero1PersistScopeOptions;
  /** Agent-only: whether the senior-tier add-field action is offered. */
  canAddField?: boolean;
  /** Agent-only: the open draft is pinned to an older run — the panel
   *  triggers the automatic refresh (unchanged-row entries are preserved). */
  baseRunStale?: boolean;
}

/** The fields an editor collects. The version token is added at the write. */
export interface Vero1ItemEdit {
  /** The agent's pick; null hands the row back to Lumen's severity. */
  overrideSeverity?: Vero1Severity | null;
  /** The client's name for the pick, when the severity offers more than one. */
  overrideOutcomeName?: string;
  reasonCode?: string;
  otherReasonLabel?: string;
  overrideExplanation?: string;
  actionForLeasing?: string;
  internalNote?: string;
}

/**
 * What actually goes over the wire. The draft's updated_at as last read is
 * required — optimistic concurrency, so a second agent's save can't be
 * clobbered, and the API rejects the write without it.
 */
export interface Vero1ItemEditRequest extends Vero1ItemEdit {
  expectedUpdatedAt: string;
}

/** Scopes the add-field picker may offer — the server omits a wider scope
 *  when it covers nothing beyond the narrower one. */
export interface Vero1PersistScopeOptions {
  property?: { name: string };
  portfolio?: { name: string; propertyCount: number; vero1ActiveCount: number };
  company?: { name: string; propertyCount: number; vero1ActiveCount: number };
}

export interface Vero1CustomItemInput {
  label: string;
  category?: string;
  description?: string;
  overrideSeverity?: Vero1Severity | null;
  reasonCode?: string;
  otherReasonLabel?: string;
  overrideExplanation?: string;
  actionForLeasing?: string;
  internalNote?: string;
  persistScope?: "property" | "portfolio" | "company" | null;
}

export interface Vero1SubmitInput {
  finalRecommendation: Vero1Recommendation;
  finalRecommendationLabel?: string;
  instructionLine?: string;
  decisionRationale?: string;
  clientFindings?: string;
  cancelReason?: Vero1CancelReason | "";
  /**
   * Attestation guard (spec 6.6): the checklist's updated_at as of opening the
   * report. With it, a checklist that moved rejects the submit (409) so the
   * agent chooses Reload vs Submit anyway; omitting it submits regardless.
   */
  expectedUpdatedAt?: string | null;
}

export interface Vero1ChangedItem {
  label: string;
  /** Applicant display name on applicant-scoped rows; null for deal-level. */
  applicant: string | null;
  criterionKey: string;
  category: string | null;
  originalSeverity: Vero1Severity | null;
  overrideSeverity: Vero1Severity | null;
  originalName: string | null;
  overrideName: string | null;
  /** Server-resolved: a same-severity rename moves the result too, so the
   *  severities alone cannot decide whether to draw the transition. */
  resultMoved?: boolean;
  /** The fact's measured value, read-only; null where jurisdiction redacts it. */
  value: unknown;
  reason: string;
  findings: string;
  actionForLeasing: string;
}

/** Deal.vero1_changed_items — null until a review is submitted. */
export interface Vero1ChangedItems {
  recommendation: Vero1Recommendation | null;
  rationale: string;
  items: Vero1ChangedItem[];
}
